import React, { useState, useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import CloseIcon from "@components/Icons/Close";
import { usePageContext } from "@providers/PageProvider";
import useWindowSize from "@hooks/useWindowSize";
import styles from "./PopupBanner.module.scss";
import service from "@utils/apiService";
import _ from "lodash";

type ImageInfo = {
  src: string;
  alt: string;
  width: number;
  height: number;
} | null;

// Minimum wait time of 5 minutes to prevent unexpected issues if server returns wrong data
const MINIMUM_WAIT_TIME_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

const PopupBanner = () => {
  const { pageData } = usePageContext();
  const [isOpen, setIsOpen] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width ? width <= 768 : false; // Common breakpoint for mobile
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get popupBanner from pageData attributes (based on actual API structure)
  const popupBanner = _.get(pageData, "attributes.popupBanner", null);

  // Extract image data from Strapi media field structure
  // Strapi media fields have structure: { data: { attributes: { url, alternativeText, width, height } } }

  const extractImageInfo = (imageData: any): ImageInfo => {
    if (!imageData) return null;
    
    const attributes = _.get(imageData, "data.attributes") || _.get(imageData, "attributes");
    if (!attributes) return null;

    const url = attributes.url;
    if (!url) return null;

    return {
      src: service.strapiUrl(url),
      alt: attributes.alternativeText || "Popup banner",
      width: attributes.width,
      height: attributes.height,
    };
  };

  // Get desktop and mobile images from popupBanner component
  const desktopImageData = _.get(popupBanner, "desktopImage", null);
  const mobileImageData = _.get(popupBanner, "mobileImage", null);
  
  const desktopImageInfo = useMemo(() => extractImageInfo(desktopImageData), [desktopImageData]);
  const mobileImageInfo = useMemo(() => extractImageInfo(mobileImageData), [mobileImageData]);
  
  // Determine which image to show with fallback - memoized to prevent unnecessary re-renders
  const activeImageInfo = useMemo(() => {
    if (isMobile) {
      // Mobile: prefer mobileImage, fallback to desktopImage
      return mobileImageInfo || desktopImageInfo;
    } else {
      // Desktop: prefer desktopImage, fallback to mobileImage
      return desktopImageInfo || mobileImageInfo;
    }
  }, [isMobile, mobileImageInfo, desktopImageInfo]);

  const link = _.get(popupBanner, "link", "");
  // initial wait time to show the banner
  const waitTime = _.get(popupBanner, "waitTime", 3) * 1000;
  // popup only displays after a sufficient interval since its last appearance
  // Apply minimum wait time to prevent unexpected issues if server returns wrong data
  const showInternal = useMemo(() => {
    const serverInterval = _.get(popupBanner, "showInterval", 300) * 1000;
    return Math.max(serverInterval, MINIMUM_WAIT_TIME_MS);
  }, [popupBanner]);
  const lastClosedKey = useMemo(() => 
    popupBanner ? `amanaki_popup_last_closed_${popupBanner.id}` : "amanaki_popup_last_closed",
    [popupBanner]
  );

  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Don't set up timer if popup is already open
    if (isOpen) {
      return;
    }

    if (process.env.NODE_ENV === "development") {
      console.log("PopupBanner - pageData:", pageData);
      console.log("PopupBanner - popupBanner:", popupBanner);
      console.log("PopupBanner - isMobile:", isMobile);
      console.log("PopupBanner - activeImageInfo:", activeImageInfo);
      console.log("PopupBanner - waitTime:", waitTime);
      console.log("PopupBanner - showInternal:", showInternal);
    }
    
    if (activeImageInfo && activeImageInfo.src && activeImageInfo.src !== "#") {
      // Check last closed time from localStorage
      const lastClosed = typeof window !== 'undefined' ? localStorage.getItem(lastClosedKey) : null;
      if (lastClosed) {
        const timePassed = Date.now() - parseInt(lastClosed, 10);
        if (timePassed < showInternal) {
          if (process.env.NODE_ENV === "development") {
            console.log(`PopupBanner - Skipped. Time passed: ${timePassed}ms, Required: ${showInternal}ms`);
          }
          return;
        }
      }

      // Delay showing popup based on waitTime
      timerRef.current = setTimeout(() => {
        setIsOpen(true);
        timerRef.current = null;
      }, waitTime);

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [popupBanner, pageData, activeImageInfo, waitTime, isMobile, showInternal, lastClosedKey, isOpen]);

  const handleClose = () => {
    // Clear any pending timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem(lastClosedKey, Date.now().toString());
    }
  };

  const handleImageClick = () => {
    if (link) {
      window.location.href = link;
      handleClose();
    }
  };

  if (!popupBanner || !activeImageInfo || !activeImageInfo.src || activeImageInfo.src === "#") {
    return null;
  }

  if (!isOpen) return null;

  const imageWidth = activeImageInfo.width || 800;
  const imageHeight = activeImageInfo.height || 600;
  const imageRatio = imageHeight ? imageWidth / imageHeight : 1;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div 
        className={styles.popupContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className={styles.imageWrapper}
          onClick={link ? handleImageClick : undefined}
          style={link ? { cursor: "pointer" } : {}}
        >
          {isMobile ? (
            <div
              className={styles.mobileMediaBox}
              style={{
                // Fit within viewport while preserving aspect ratio.
                width: `min(100vw, calc(100vh * ${imageRatio}))`,
                height: `min(100vh, calc(100vw / ${imageRatio}))`,
              }}
            >
              <button
                className={`${styles.closeButton} ${styles.closeButtonMobile}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                aria-label="Close popup"
              >
                <CloseIcon color="black" size={24} />
              </button>
              <Image
                src={activeImageInfo.src}
                alt={activeImageInfo.alt}
                fill
                sizes="100vw"
                className={styles.image}
                style={{ objectFit: "contain" }}
                priority
              />
            </div>
          ) : (
            <>
              <button
                className={`${styles.closeButton} ${styles.closeButtonDesktop}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                aria-label="Close popup"
              >
                <CloseIcon color="black" size={24} />
              </button>
              <Image
                src={activeImageInfo.src}
                alt={activeImageInfo.alt}
                width={imageWidth} // Fallback width
                height={imageHeight} // Fallback height
                // Keep desktop behavior: preserve aspect ratio within the container
                style={{ width: "100%", height: "auto" }}
                className={styles.image}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PopupBanner;

